import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CustomModal } from "@modules/core/components/base-modals";
import { ApicallOption, ApicallService, ApiVersion } from "@core-services/apicall.service";
import { Helper, parseList } from "@helpers/functions";
import { Questionnaire } from "@models/questionnaire";
import { QuestionnaireQuestion } from "@models/questionnaire_question";
import { QuestionnaireAnswer } from "@models/questionnaire_answer";
import { EMPTY, Observable } from "rxjs";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import {
	APPROPRIATENESS_BLOCKED_ERROR,
	APPROPRIATENESS_COOLDOWN_ERROR,
	APPROPRIATENESS_COOLOFF_ERROR,
	END_CRYPTO_JOURNEY,
	HIGH_NET_INVESTOR_ANSWER,
	HIGH_NET_INVESTOR_FLOW,
	HIGH_NET_INVESTOR_QUESTION_FOUR,
	HIGH_NET_INVESTOR_QUESTION_ONE,
	HIGH_NET_INVESTOR_QUESTION_SIX,
	HIGH_NET_INVESTOR_QUESTION_THREE,
	HIGH_NET_INVESTOR_QUESTION_TWO,
	INVESTOR_PROFILE_QUESTION,
	NEITHER_ANSWER,
	NOT_AN_INVESTOR_FLOW,
	RESTRICTED_INVESTOR_ANSWER,
	RESTRICTED_INVESTOR_CONFIRMATION,
	RESTRICTED_INVESTOR_FLOW,
	RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE,
	RESTRICTED_INVESTOR_FUTURE_QUESTION_TWO,
	RESTRICTED_INVESTOR_PAST_QUESTION_ONE,
	RESTRICTED_INVESTOR_PAST_QUESTION_TWO,
	RESTRICTED_INVESTOR_STATEMENT
} from "@helpers/constants";
import { NewAccountFacade } from "@modules/new_account/domain/+state/new_account.facade";
import { NewAccount } from "@modules/new_account/domain/entities/new_account.model";
import { CustomerSegmentationService } from "@modules/uk-finprom/components/customer-segmentation/customer-segmentation.service";
import { config, ENVS } from "@config";
import { SegmentService } from "@main-services/segment.service";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IAppropriatenessTestResponse {}

export enum FlowType {
	RESTRICTED = RESTRICTED_INVESTOR_FLOW,
	HIGH = HIGH_NET_INVESTOR_FLOW
}

@Component({
	selector: "customer-segmentation",
	templateUrl: "./customer-segmentation.component.html",
	styleUrls: ["./customer-segmentation.component.scss", "../../../../theme/shared/shared-modal.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CustomerSegmentationModalComponent extends CustomModal implements OnInit {
	public step: number = 1;
	public questionnaire: Questionnaire = new Questionnaire();
	public accountState$: Observable<NewAccount> = EMPTY;
	public flowId: number = RESTRICTED_INVESTOR_FLOW;
	private questionsMapping = {
		1: {
			2: [INVESTOR_PROFILE_QUESTION],
			3: [RESTRICTED_INVESTOR_STATEMENT],
			4: [RESTRICTED_INVESTOR_PAST_QUESTION_ONE, RESTRICTED_INVESTOR_PAST_QUESTION_TWO],
			5: [RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE, RESTRICTED_INVESTOR_FUTURE_QUESTION_TWO],
			6: [RESTRICTED_INVESTOR_CONFIRMATION],
			7: [END_CRYPTO_JOURNEY]
		},
		2: {
			2: [INVESTOR_PROFILE_QUESTION],
			3: [
				HIGH_NET_INVESTOR_QUESTION_ONE,
				HIGH_NET_INVESTOR_QUESTION_TWO,
				HIGH_NET_INVESTOR_QUESTION_THREE,
				HIGH_NET_INVESTOR_QUESTION_FOUR
			],
			4: [HIGH_NET_INVESTOR_QUESTION_SIX],
			5: [END_CRYPTO_JOURNEY]
		},
		3: {
			2: [INVESTOR_PROFILE_QUESTION],
			3: [END_CRYPTO_JOURNEY]
		}
	};
	// STG
	// flowId: newQuestionId
	private stgQuestionIdFlowFilter = {
		1: ["147", "148", "149", "150", "151", "152", "153", "159"],
		2: ["147", "154", "155", "156", "157", "158", "159"],
		3: ["147", "159"]
	};
	// oldId : newId
	private stgQuestionIdMapping = {
		"32": "147",
		"33": "148",
		"34": "149",
		"35": "150",
		"36": "151",
		"37": "152",
		"38": "153",
		"39": "154",
		"40": "155",
		"41": "156",
		"42": "157",
		"43": "158",
		"44": "159"
	};
	// oldId : newId
	private stgAnswerIdMapping = {
		"139": "394",
		"140": "395",
		"141": "396",
		"142": "397",
		"143": "398",
		"144": "399",
		"145": "400",
		"146": "401",
		"147": "402",
		"148": "403",
		"149": "404",
		"150": "405",
		"151": "406",
		"152": "407",
		"153": "408",
		"154": "409",
		"155": "410",
		"156": "411"
	};
	// PROD
	// flowId: newQuestionId
	private prodQuestionIdFlowFilter = {
		1: ["106", "107", "108", "109", "110", "111", "112", "118"],
		2: ["106", "113", "114", "115", "116", "117", "118"],
		3: ["106", "118"]
	};
	// oldId : newId
	private prodQuestionIdMapping = {
		"24": "106",
		"25": "107",
		"26": "108",
		"27": "109",
		"28": "110",
		"29": "111",
		"30": "112",
		"31": "113",
		"32": "114",
		"33": "115",
		"34": "116",
		"35": "117",
		"36": "118"
	};
	// oldId : newId
	private prodAnswerIdMapping = {
		"99": "335",
		"100": "336",
		"101": "337",
		"102": "338",
		"103": "339",
		"104": "340",
		"105": "341",
		"106": "342",
		"107": "343",
		"108": "344",
		"109": "345",
		"110": "346",
		"111": "347",
		"112": "348",
		"113": "349",
		"114": "350",
		"115": "351",
		"116": "352",
		"117": "353"
	};
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	private getQuestionIdFlowFilter() {
		return config.envName === ENVS.STAGING || config.envName === ENVS.LOCAL
			? this.stgQuestionIdFlowFilter
			: this.prodQuestionIdFlowFilter;
	}
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	private getQuestionIdMapping() {
		return config.envName === ENVS.STAGING || config.envName === ENVS.LOCAL
			? this.stgQuestionIdMapping
			: this.prodQuestionIdMapping;
	}
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	private getAnswerIdMapping() {
		return config.envName === ENVS.STAGING || config.envName === ENVS.LOCAL
			? this.stgAnswerIdMapping
			: this.prodAnswerIdMapping;
	}

	// Removes the answer for "Neither statement applies to me"
	private filteredAnswersLabel = ["uk_customer_segmentation_1_general_neither"];

	private apiVersion: ApiVersion = "v2";

	@Input() public callback: Function;

	@Output() public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

	public constructor(
		private apicallService: ApicallService,
		private accountFacade: NewAccountFacade,
		protected confirmModalService: ConfirmModalService,
		private customerSegmentationService: CustomerSegmentationService,
		private readonly segmentService: SegmentService
	) {
		super();
	}

	ngOnInit(): void {
		this.accountState$ = this.accountFacade.getAccount();
		this.fetchQuestions();
		this.segmentService.track({ name: "Investor Profile Viewed - Client Categorization - Web" });
	}

	/*
		Steps
	 */
	public isStep(step: number): boolean {
		return this.step === step;
	}

	public notAnRestrictedInvestor(): boolean {
		return this.customerSegmentationService.noOptionSelected(this.flowId);
	}

	public getCurrentQuestions(flowId: FlowType, step: number): QuestionnaireQuestion[] {
		const labels: string[] = this.questionsMapping[flowId][step];

		return !this.isIntroStep() && this.questionnaire.getQuestions().filter((q) => labels.includes(q.getLabel()));
	}

	public isIntroStep(): boolean {
		return this.step === 1;
	}

	public isLastQuestion(): boolean {
		const questionsLabels = this.questionsMapping[this.flowId];
		let labelsLength = Object.keys(questionsLabels).length;

		if (this.notAnRestrictedInvestor()) {
			labelsLength = labelsLength + 1;
		}

		return this.step === labelsLength;
	}

	public close(): void {
		this.onClose.emit(true);
		super.close();
	}

	public showBackButton(): boolean {
		return !this.isIntroStep();
	}

	public backClick(): void {
		const questions = this.getCurrentQuestions(this.flowId, this.step);
		questions.forEach((question) => {
			this.resetAnswers(question);
			this.customerSegmentationService.inputFieldsSubject.next({});
		});

		if (this.step > 0) {
			this.step--;
		}
	}

	public changeAnswers(): void {
		this.step = 2;
		this.questionnaire.getQuestions().forEach((question) => this.resetAnswers(question));
		this.customerSegmentationService.inputFieldsSubject.next({});
	}

	public resetAnswers(question: QuestionnaireQuestion): void {
		if (question.isInputField()) {
			question.inputValue = "";
		}

		if (question.isSingleChoice()) {
			question.getAnswers().forEach((answer) => answer.setSelected(false));
		}
	}

	public onContinue(): void {
		this.questionnaire.getQuestions().map((q) => {
			q.getAnswers().filter((a) => {
				if (a.isSelected()) {
					if (a.getLabel() === RESTRICTED_INVESTOR_ANSWER) {
						this.flowId = RESTRICTED_INVESTOR_FLOW;
					}

					if (a.getLabel() === HIGH_NET_INVESTOR_ANSWER) {
						this.flowId = HIGH_NET_INVESTOR_FLOW;
					}

					if (a.getLabel() === NEITHER_ANSWER) {
						this.flowId = NOT_AN_INVESTOR_FLOW;
					}
				}
			});
		});

		if (this.isLastQuestion()) {
			// Confirm button
			if (!this.notAnRestrictedInvestor()) {
				this.segmentService.track({ name: "Confirm Button Clicked - Client Categorization - Web" });
			}
			this.close();
			this.sendUserResponses();
		} else {
			// Before moving to next step, we track the flow
			if (this.step == 2) {
				switch (this.flowId) {
					case RESTRICTED_INVESTOR_FLOW:
						this.segmentService.track({
							name: "Restricted Investor Clicked - Client Categorization - Web"
						});
						break;
					case HIGH_NET_INVESTOR_FLOW:
						this.segmentService.track({
							name: "High Net Worth Investor Clicked - Client Categorization - Web"
						});
						break;
					case NOT_AN_INVESTOR_FLOW:
						this.segmentService.track({ name: "Neither Clicked - Client Categorization - Web" });
						break;
					default:
						break;
				}
			}
			this.step++;
		}
	}

	public getButtonKey(): string {
		const question = this.getCurrentQuestions(this.flowId, this.step)[0];
		const buttonKeys = [
			RESTRICTED_INVESTOR_STATEMENT,
			RESTRICTED_INVESTOR_CONFIRMATION,
			HIGH_NET_INVESTOR_QUESTION_SIX,
			NEITHER_ANSWER
		];

		if (question && buttonKeys.includes(question.getLabel())) {
			return question.getAnswers()[0].getAnswer();
		}

		if (this.isIntroStep()) {
			return "stocks_test_button_continue";
		}

		return "stocks_test_button_next";
	}

	private getCurrentQuestion(): QuestionnaireQuestion {
		return this.questionnaire.getQuestions()[this.step - 2];
	}

	public isButtonDisabled(): boolean {
		const question = this.getCurrentQuestions(this.flowId, this.step)[0];
		const buttonEnabled = [
			RESTRICTED_INVESTOR_STATEMENT,
			RESTRICTED_INVESTOR_CONFIRMATION,
			HIGH_NET_INVESTOR_QUESTION_SIX,
			NEITHER_ANSWER
		];

		const isStep3High = this.isStep(3) && FlowType.HIGH;
		const isStep4Restricted = this.isStep(4) && FlowType.RESTRICTED;
		const isStep5Restricted = this.isStep(5) && FlowType.RESTRICTED;

		if (question && buttonEnabled.includes(question.getLabel())) {
			return false;
		}

		if (isStep3High || isStep4Restricted || isStep5Restricted) {
			return this.customerSegmentationService.nextButtonSubject.getValue();
		}

		if (!this.getCurrentQuestion()) {
			return false;
		}
		return !this.getCurrentQuestion()
			.getAnswers()
			.some((a) => a.isSelected());
	}

	/*
		API data
	 */
	private fetchQuestions(): void {
		const option = new ApicallOption(Questionnaire);
		option.apiVersion = this.apiVersion;
		this.apicallService
			.get("questionnaires/label/uk_customer_segmentation_appropriateness_test", option)
			.subscribe({
				next: (q: Questionnaire[]) => {
					this.questionnaire = q[0];
					const questions = parseList(this.questionnaire.getQuestions(), QuestionnaireQuestion);
					this.questionnaire.setQuestions(questions);

					// Parse answers
					this.questionnaire.getQuestions().forEach((q) => {
						const answers = parseList(q.getAnswers(), QuestionnaireAnswer);
						const filteredAnswers = answers.filter(
							(a) => !this.filteredAnswersLabel.includes(a.getLabel())
						);
						q.setAnswers(filteredAnswers);
					});
					this.sortApiData();
				},
				error: (error) => {
					Helper.handleError(error);
					if (
						error.containsCode(APPROPRIATENESS_COOLDOWN_ERROR) ||
						error.containsCode(APPROPRIATENESS_COOLOFF_ERROR) ||
						error.containsCode(APPROPRIATENESS_BLOCKED_ERROR)
					) {
						this.close();
					}
				}
			});
	}

	private sortApiData(): void {
		// Sort the questions
		this.questionnaire.getQuestions().sort((a, b) => a.getSort() - b.getSort());
		// Sort the answers
		this.questionnaire.getQuestions().forEach((q) => {
			// Filter for valid:
			const answers = q.getAnswers().filter((a) => a.isValid());
			q.setAnswers(answers);
			// Sort answers
			q.getAnswers().sort((a, b) => a.getSort() - b.getSort());
		});
	}

	private sendUserResponses(): void {
		const userResponses = this.questionnaire.getQuestions().map((q) => {
			const questionId = q.getId();
			const transformedQuestionId = this.getQuestionIdMapping()[questionId];

			if (q.isInputField() && q.inputValue == "" && q.getAnswers().length == 1) {
				const answer_id = q.getAnswers().map((a) => a.getId())[0];
				return {
					question_id: transformedQuestionId,
					answer_value: null,
					answer_id: answer_id
				};
			}

			var answer_value = null;
			var answer_id = null;
			if (q.isInputField() && q.inputValue != "") {
				const placeholder = q.getInputPlaceholder();
				if (placeholder.includes("%")) {
					answer_value = q.inputValue + "%";
				} else {
					answer_value = q.inputValue;
				}
			}

			if (q.getAnswers().length == 1) {
				answer_id = q.getAnswers().map((a) => a.getId())[0];
			} else {
				// User's input content
				const selectedAnswers = q.getAnswers().filter((a) => a.isSelected()); // Ticked answers by user
				const answers = selectedAnswers.map((a) => a.getId()); // Submit the answer "id" only

				if (answers.length > 0) {
					answer_id = answers[0];
				}
			}

			const transformedAnswerId = this.getAnswerIdMapping()[answer_id];

			return {
				question_id: transformedQuestionId,
				answer_value: answer_value,
				answer_id: transformedAnswerId
			};
		});
		const data = {
			client_categorization: userResponses.filter(
				(r) => this.getQuestionIdFlowFilter()[this.flowId].includes(r.question_id) && r.answer_id != null
			)
		};
		const option = new ApicallOption(null, data);
		// API call once all questions are answered
		option.apiVersion = "v1";
		this.apicallService.post("questionnaires-onboarding/questionnaires/client-categorization", option).subscribe({
			next: (res: IAppropriatenessTestResponse) => {
				this.close();
				if (this.callback) {
					this.callback();
				}

				// End my crypto journey
				if (this.notAnRestrictedInvestor()) {
					window.location.replace("/user/logout");
				} else {
					window.location.reload();
				}
			},
			error: (error) => {
				// If answers are submitted but user didn't pass the test, close the modal
				if (
					error.containsCode(APPROPRIATENESS_COOLDOWN_ERROR) ||
					error.containsCode(APPROPRIATENESS_COOLOFF_ERROR)
				) {
					this.close();
				}
			}
		});
	}
}
